import { Injectable } from '@angular/core';
import { BaseHttpService } from '../base-api-service/base-http.service';
import { map, Observable } from 'rxjs';
import { HttpHeaders, HttpParams } from '@angular/common/http';
import { environment } from '../../../environments/environment';
import { Widget_Data } from '../data/interface/widget.interface';
import { CommonService } from '../common/common.service';
import { DeclineReasonPayload } from '../../../shared/property-ineligibility-modal/model/modal.model';

interface UnderwriterCommentPayload {
  txn_id: string;
  quote_id: string;
  notes: string;
}

@Injectable({
  providedIn: 'root'
})

export class SubmissionsService {

  private readonly API_BASE = environment.platformUrl;
  private readonly baseUrl = environment.api_url;
  private readonly appId = environment.app_id;

  constructor(private http: BaseHttpService, private commonService: CommonService) { }

  /** Helper to build default headers */
  private buildHeaders(options?: {
    includeTenant?: boolean;
    includeEmail?: boolean;
  }): HttpHeaders {

    let headers = new HttpHeaders({
      'Content-Type': 'application/json',
      'app-id': this.appId
    });

    if (options?.includeTenant) {
      headers = headers.set('tenant-id', environment.tenant_id);
    }

    if (options?.includeEmail) {
      headers = headers.set('x-email-id', this.commonService.getEmail());
    }

    return headers;
  }



  /** Helper to build default params */
  private buildParams(paramsObj: Record<string, string | number>): HttpParams {
    let params = new HttpParams();
    Object.keys(paramsObj).forEach(key => {
      params = params.set(key, paramsObj[key].toString());
    });
    return params;
  }

  // Validate tenant
  validateTenant(tenantCode: string): Observable<any> {
    const params = this.buildParams({ tenant_code: tenantCode });
    const headers = this.buildHeaders();
    return this.http.get<any>(`${this.API_BASE}api/v1/validate/tenant`, params, headers);
  }

  // Register agent/user
  registerAgent(payload: any): Observable<any> {
    return this.http.post<any>(
      `${this.baseUrl}agency/register`,
      payload,
      this.buildHeaders({ includeTenant: true })
    );
  }

  underwritingQA(): Observable<any> {
    return this.http.get<any>('assets/mock/underwritingQA.json');
  }

  getSubmissionById(submissionId: string, txnId: string): Observable<any> {
    const params = this.buildParams({
      submission_id: submissionId,
      txn_id: txnId
    });
    
    return this.http.get<any>(
      `${this.baseUrl}view-submission`,
      params,
      this.buildHeaders({
        includeTenant: true,
        includeEmail: true
      })
    );
    // return this.http.get<any>('assets/mock/view-submission.mock.json');
  }


getSubmissionDetails(submissionId: string, txnId?: string): Observable<any> {
    const params = this.buildParams({
      submission_id: submissionId,
      // txn_id: txnId
    });
    
    return this.http.get<any>(
      `${this.baseUrl}view-submission`,
      params,
      this.buildHeaders({
        includeTenant: true,
        includeEmail: true
      })
    );
    // return this.http.get<any>('assets/mock/view-submission.mock.json');
  }

  getMySubmissions(
    fromDate: string | null = null,
    toDate: string | null = null
  ): Observable<any> {

    const params: { [key: string]: string } = {};

    if (fromDate && toDate) {
      params['from_date'] = fromDate;
      params['to_date'] = toDate;
    }

    return this.http.get<any>(
      `${this.baseUrl}dashboard/submissions`,
      this.buildParams(params),
      this.buildHeaders({
        includeTenant: true,
        includeEmail: true
      })
    );
  }

  //Ops Submission
  getOpsSubmissions(
    fromDate: string | null = null,
    toDate: string | null = null,
    isArchived: boolean = false
  ): Observable<any> {

    const params: { [key: string]: string } = {};

    if (fromDate && toDate) {
      params['from_date'] = fromDate;
      params['to_date'] = toDate;
    }
    if (isArchived) {
      params['action'] = 'archived';
    }

    return this.http.get<any>(
      `${this.baseUrl}ops-submissions`,
      this.buildParams(params),
      this.buildHeaders({
        includeTenant: true,
        includeEmail: true
      })
    );
  }


  submitBasicDetails(payload: any): Observable<any> {
    return this.http.post<any>(
      `${this.baseUrl}basic-details`,
      payload,
      this.buildHeaders({ includeTenant: true, includeEmail: true })
    );
  }

  getBasicDetails(submissionId: string, txnId: string, quoteId: string): Observable<any> {
    const params = this.buildParams({
      submission_number: submissionId,
      ...(txnId && { txn_id: txnId }),
      ...(quoteId && { quote_id: quoteId })
    });

    return this.http.get<any>(
      `${this.baseUrl}basic-details`,
      params,
      this.buildHeaders({ includeTenant: true, includeEmail: true })
    );
  }


  public getSubmissionWidgets(fromDate?: string | null, toDate?: string | null) {


    let params = new HttpParams();

    if (fromDate) {
      params = params.set('from_date', fromDate);
    }

    if (toDate) {
      params = params.set('to_date', toDate);
    }

    return this.http.get<any>(
      `${this.baseUrl}dashboard/submission-summary`,
      params,
      this.buildHeaders({ includeTenant: true, includeEmail: true })
    );
  }

  //For Ops submission
  public getOpsSubmissionWidgets(fromDate?: string | null, toDate?: string | null) {


    let params = new HttpParams();

    if (fromDate) {
      params = params.set('from_date', fromDate);
    }

    if (toDate) {
      params = params.set('to_date', toDate);
    }

    return this.http.get<any>(
      `${this.baseUrl}ops-submission-summary`,
      params,
      this.buildHeaders({ includeTenant: true, includeEmail: true })
    );
  }


  submitSectionProgress(submissionId: string, sectionId: string, payload: any): Observable<any> {
    const headers = this.buildHeaders({ includeTenant: true, includeEmail: true });

    const body = {
      submission_id: submissionId,
      section_id: sectionId,
      data: payload
    };

    return this.http.post<any>(
      `${this.baseUrl}application-progress`,
      body,
      headers
    );
  }

  createUserSubmission(): Observable<any> {
    //if user is not founde send the email to create the user and submission together
    const headers = this.buildHeaders({ includeTenant: true });

    return this.http.post<any>(
      `${this.baseUrl}user`,
      headers
    );
  }

  modifyStatusOrSubmissionType(
    body: any = {},
    txnId: string,
    quoteId?: string
  ): Observable<any> {

    const headers = this.buildHeaders({ includeTenant: true });
    const params = this.buildParams({ txn_id: txnId, ...(quoteId && { quote_id: quoteId }) });

    return this.http.put(
      `${this.baseUrl}submission/status`,
      body,
      headers,
      params
    );
  }

  confirmQuote(
    body: any = {},
  ): Observable<any> {

    return this.http.put(
      `${this.baseUrl}confirm-quote`,
      body,
    );
  }

  

  getUnderwritingReviewSubmissionDetails(txnId: string, quoteId: string): Observable<any> {
    const params = this.buildParams({
      quote_id: quoteId,
      txn_id: txnId
    });

    return this.http.get<any>(
      `${this.baseUrl}view-underwriter`,
      params,
      this.buildHeaders({
        includeTenant: true,
        includeEmail: true
      })
    );
  }

  // get uw comments



  getUnderwriterComments(quoteId: string): Observable<any> {
    const params = this.buildParams({
      quote_id: quoteId,
    });

    return this.http.get<any>(
      `${this.baseUrl}notes`,
      params,
      this.buildHeaders({
        includeTenant: true,
        includeEmail: true
      })
    );
  }

  postUnderwriterComments(payload: UnderwriterCommentPayload): Observable<any> {
    return this.http.post<any>(
      `${this.baseUrl}notes`,
      payload,
      this.buildHeaders({ includeTenant: true })
    );
  }

  overrideAndDecline(payload: any): Observable<any> {
    return this.http.post<any>(
      `${this.baseUrl}underwriter-review`,
      payload,
      this.buildHeaders({ includeTenant: true })
    );
  }

  //producerflow check based on the role we need to redirect to different pages so this api will return the role of the user and based on that we can decide where to redirect the user
  producerFlowCheckUserDetails(): Observable<any> {
    const headers = this.buildHeaders({  includeEmail: true });

    return this.http.get<any>(
        `${this.baseUrl}user-details`,
        this.buildParams({}),
        headers
    );
  }

  
  /**
   * Fetches the status history for a given transaction.
   * GET submission/status?txn_id=<txnId>
   */
 getSubmissionStatus(txnId: string, quoteId: string, accessToken?: string): Observable<any> {
  const params = this.buildParams({
    txn_id: txnId,
    quote_id: quoteId,
  });

  let headers = this.buildHeaders({
    includeTenant: true,
    includeEmail: true
  });
  if (accessToken) {
    headers = headers.set('Authorization', `Bearer ${accessToken}`);
  }

  return this.http.get<any>(
    `${this.baseUrl}submission/progress`,
    params,
    headers
  );
}
  cloneSubmission(txnId: string, quoteId: string): Observable<any> {
  const headers = this.buildHeaders({ includeTenant: true, includeEmail: true });

  const body = {
    txn_id: txnId,
    quote_id: quoteId
  };

  return this.http.post<any>(
    `${this.baseUrl}clone-submission`,
    body,
    headers
  );
}

  archiveSubmission(txnId: string, quoteId: string): Observable<any>{
    const headers = this.buildHeaders({ includeTenant: true, includeEmail: true });
    const params = this.buildParams({
      txn_id: txnId,
      quote_id: quoteId
    });

    return this.http.put<any>(
      `${this.baseUrl}submission/withdraw`,
      {},
      headers,
      params
    );
  }

  addDeclineReasons(txnId: string, quoteId: string, reasons: DeclineReasonPayload[]): Observable<any> {
  const headers = this.buildHeaders({ includeTenant: true, includeEmail: true })
  const params = this.buildParams({ txn_id: txnId, quote_id: quoteId });

  return this.http.post<any>(
    `${this.baseUrl}submission/decline-reasons`,   
    reasons,
    headers,
    params
  );
}
 
}
